<template>
  <div class="page">
    <n-loading-spinner v-if="loading" />
    <section class="section content-container" v-else>
      <div class="container is-fluid">
        <div class="columns">
          <div class="column">
            <div class="island">
              <nav class="level">
                <div class="level-left"></div>
                <div class="level-right">
                  <p class="level-item">
                    <n-button @click="addIntent()" variant="primary">
                      <span>Add Intent</span>
                    </n-button>
                  </p>
                </div>
              </nav>
              <n-data-table
                :columns="tableColumns"
                @cellClicked="editIntent"
                class="table is-fullwidth intents"
                :items="sortedIntents"
              >
                <template v-slot:nodata>
                  <div class="no-intents">
                    <font-awesome-icon icon="search" class="no-intents__icon" size="2x"></font-awesome-icon>
                    <div class="no-intents__message">
                      <h1>No intents found</h1>
                    </div>
                  </div>
                </template>
                <template v-slot:actions="{ row }">
                  <DropDownMenu :goLeft="true" dropDownIcon="dots">
                    <a @click="deleteIntent(row)" class="dropdown-item">
                      <span>
                        <DeleteIcon></DeleteIcon>
                      </span>
                      <span class="action-label">Delete</span>
                    </a>
                  </DropDownMenu>
                </template>
              </n-data-table>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AddIntentModal
      v-if="showAddModal"
      ref="addIntentModal"
      @completed="showAddModal = false"
      @closed="showAddModal = false"
    />
    <DeleteIntentModal
      v-if="showDeleteModal"
      :intent="intentToDelete"
      @cancel="intentToDelete = undefined"
      @deleted="intentToDelete = undefined"
      @failed-to-delete="intentToDelete = undefined"
    />

    <EditIntentModal
      v-if="showEditModal"
      :intent-id="intentToEdit.id"
      @closed="intentToEdit = undefined"
      @completed="onEditCompleted"
      @failed-to-load="intentToEdit = undefined"
    />
  </div>
</template>

<script lang="ts" setup>
import Toast from '@/shared/services/toast';
import { IntentListItemViewModel } from '../models/intents/intentListItemViewModel';
import AddIntentModal from '@/features/bots/components/designer/AddIntentModal.vue';
import { LOAD_INTENTS } from '@/store/intents/actionTypes';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import EditIntentModal from '@/features/bots/components/designer/EditIntentModal.vue';
import DeleteIntentModal from '@/features/bots/components/designer/DeleteIntentModal.vue';
import DropDownMenu from '@/shared/components/dropDownMenu/DropDownMenu.vue';
import DeleteIcon from 'vue-material-design-icons/TrashCanOutline.vue';
import { useStore } from 'vuex';
import { computed, nextTick, onMounted, ref } from 'vue';

library.add(faSearch);

const store = useStore();
const intents = computed<IntentListItemViewModel[]>(() => store.getters.intents);

const loadIntents = (forceLoad?: boolean): Promise<void> => store.dispatch(LOAD_INTENTS, forceLoad);

const loading = ref(false);
const intentToEdit = ref<IntentListItemViewModel>();
const intentToDelete = ref<IntentListItemViewModel>();
const addIntentModal = ref<typeof AddIntentModal | null>(null);

const showAddModal = ref(false);
const showEditModal = computed(() => !!intentToEdit.value);
const showDeleteModal = computed(() => !!intentToDelete.value);
const sortedIntents = computed<IntentListItemViewModel[]>(() =>
  intents.value.sort((a: IntentListItemViewModel, b: IntentListItemViewModel) => a.title.localeCompare(b.title)),
);

const tableColumns = [
  {
    display: 'Intent',
    name: 'title',
    clickable: true,
  },
  {
    display: 'Utterances',
    name: 'utteranceCount',
  },
];

onMounted(async () => await getIntents());

async function onEditCompleted(): Promise<void> {
  intentToEdit.value = undefined;
  intentToDelete.value = undefined;
  await getIntents();
}

async function getIntents(): Promise<void> {
  loading.value = true;
  try {
    await loadIntents(true);
  } catch (error: any) {
    Toast.showError(error.message);
  }
  loading.value = false;
}

async function addIntent() {
  showAddModal.value = true;
  await nextTick(() => {
    if (addIntentModal.value) {
      addIntentModal.value.addNewIntent();
    }
  });
}

function editIntent(intent: IntentListItemViewModel) {
  intentToEdit.value = intent;
}

function deleteIntent(intent: IntentListItemViewModel) {
  intentToDelete.value = intent;
}
</script>
<style lang="scss" scoped>
.no-intents {
  text-align: center;
  height: 15vh;
  color: $color-netcall-body-text;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  &__icon {
    margin: 0.5rem;
    display: inline-block;
  }
  &__message {
    margin-top: 1rem;
  }
}
.intents {
  :deep(.n-table tr td:nth-of-type(1)) {
    width: 80%;
  }
}
.dropdown-item {
  .action-label {
    margin-left: 1em;
  }
}
</style>
